import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Calendar, FileText, Hash } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchBookById, clearCurrentBook } from '../../store/books/booksSlice';
import { Button, LoadingSpinner } from '../../components/common';

export const BookDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentBook, isLoading } = useAppSelector((state) => state.books);

  useEffect(() => {
    if (id) {
      dispatch(fetchBookById(id));
    }
    return () => {
      dispatch(clearCurrentBook());
    };
  }, [dispatch, id]);

  if (isLoading || !currentBook) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <button
        className="flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base text-slate-600 hover:text-slate-900 mb-4 sm:mb-6 transition-colors"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        Back to Books
      </button>

      <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8">
        <div className="flex flex-col md:flex-row gap-4 sm:gap-6 md:gap-8">
          <div className="w-full max-w-[200px] mx-auto md:mx-0 md:w-64 lg:w-72 flex-shrink-0">
            {currentBook.coverImage ? (
              <img
                src={currentBook.coverImage}
                alt={currentBook.title}
                className="w-full rounded-lg shadow-md"
              />
            ) : (
              <div className="aspect-[3/4] bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                <span className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white">{currentBook.title.charAt(0)}</span>
              </div>
            )}
          </div>

          <div className="flex-1 text-center md:text-left">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-1 sm:mb-2">{currentBook.title}</h1>
            <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-4 sm:mb-6">by {currentBook.author}</p>

            <div className="flex flex-wrap justify-center md:justify-start gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
              {currentBook.category && (
                <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-slate-600 bg-slate-100 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                  <FileText size={14} className="sm:w-4 sm:h-4" />
                  {currentBook.category}
                </span>
              )}
              {currentBook.publishedYear && (
                <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-slate-600 bg-slate-100 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                  <Calendar size={14} className="sm:w-4 sm:h-4" />
                  {currentBook.publishedYear}
                </span>
              )}
              {currentBook.pageCount > 0 && (
                <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-slate-600 bg-slate-100 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                  <BookOpen size={14} className="sm:w-4 sm:h-4" />
                  {currentBook.pageCount} pages
                </span>
              )}
              <span className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-slate-600 bg-slate-100 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
                <Hash size={14} className="sm:w-4 sm:h-4" />
                {currentBook.isbn}
              </span>
            </div>

            {currentBook.description && (
              <div className="mb-4 sm:mb-6">
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1 sm:mb-2">Description</h3>
                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{currentBook.description}</p>
              </div>
            )}

            {currentBook.fileUrl && (
              <div className="flex justify-center md:justify-start">
                <Button
                  onClick={() => navigate(`/books/${currentBook._id}/read`)}
                  className="w-full sm:w-auto"
                >
                  <BookOpen size={18} className="mr-2 sm:w-5 sm:h-5" />
                  Read Book
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
